<!--
  @fileoverview GenAI Citations Component (Vue 3)

  Displays citation pills/badges for GenAI responses that reference
  knowledge base documents. Users can click on citations to see
  more details in a modal.

  @component GenAICitations
  @displayName GenAI Source Citations

  @example
  <GenAICitations :citations="citationData" />
-->
<template>
  <div>
    <hr />
    <div class="mb-2 flex items-center">
      <el-icon class="mr-[6px]"><InfoFilled /></el-icon>
      <span class="font-medium">Sources:</span>
    </div>
    <div class="citation-container">
      <div
        class="citation-pill"
        v-for="(citation, index) in citations.data"
        @click="showCitationDetails(citation, index)"
        :key="index"
      >
        <span class="pill-text" v-if="loadingCitationIndex !== index">
          {{ citation.source }}
        </span>
        <div class="pill-text loading-animation" v-else>
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    </div>

    <GenAICitationModal
      v-if="selectedCitation"
      :visible="shouldShowModal"
      @closeModal="closeModal"
      :citation="selectedCitation"
      :showFullSource="citations.showFullSource"
    />
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/GenAICitations
 * @description Displays citation sources for GenAI responses (Vue 3 Composition API)
 */
import { ref } from "vue";
import { InfoFilled } from "@element-plus/icons-vue";
import { useMainStore } from "@/stores/main";
import GenAICitationModal from "./GenAICitationModal.vue";

/**
 * Component props
 */
const props = defineProps({
  /**
   * Citations object containing data array and display options
   * @type {{ data: Array<{ source: string, [key: string]: any }>, showFullSource: boolean }}
   */
  citations: {
    type: Object,
    required: true,
  },
});

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Local state
 */
const shouldShowModal = ref(false);
const selectedCitation = ref(null);
const loadingCitationIndex = ref(null);

/**
 * Close the citation modal
 */
function closeModal() {
  shouldShowModal.value = false;
  selectedCitation.value = null;
}

/**
 * Show citation details in modal
 *
 * Fetches detailed citation information from the server
 * before displaying the modal.
 *
 * @param {Object} citation - The citation to show details for
 * @param {number} index - Index of the citation in the array
 */
async function showCitationDetails(citation, index) {
  loadingCitationIndex.value = index;
  try {
    const response = await store.getCitationDetails(citation);
    selectedCitation.value = {
      ...citation,
      ...response,
    };
    shouldShowModal.value = true;
  } finally {
    loadingCitationIndex.value = null;
  }
}
</script>

<style scoped>
.citation-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.citation-pill {
  background-color: #f2f3f5;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 14px;
  color: #333;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  max-width: 200px;
  min-width: 170px;
  overflow: hidden;
  cursor: pointer;
}

.pill-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  display: inline-block;
}

.year {
  margin-left: 6px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
}

.loading-animation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 18px;
  width: 100%;
  max-width: 100%;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #888;
  animation: bounce 1.4s infinite ease-in-out both;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}
.dot:nth-child(2) {
  animation-delay: -0.16s;
}
.dot:nth-child(3) {
  animation-delay: 0;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.3;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
